import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { AuthStore } from '../state/auth.store';
import { ApiAuthResponse } from '../models/user.model';

/** Starea reimprospatarii tokenului, comuna tuturor cererilor. */
@Service()
export class TokenRefreshState {
    isRefreshing = false;
    // null cat timp reimprospatarea e in curs, apoi true (token nou) sau false (esuata, logout)
    readonly refreshed = new BehaviorSubject<boolean | null>(null);
}

function addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
}

function isTokenError(error: HttpErrorResponse): boolean {
    const detail = error.error?.detail;
    // corp necunoscut (ex. blob la descarcari): tratam ca token expirat, ca inainte
    return typeof detail !== 'string' || detail === 'Could not validate credentials';
}

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
    if (req.url.includes('/token')) {
        return next(req);
    }

    const authStore = inject(AuthStore);
    const authServ = inject(AuthService);
    const refreshState = inject(TokenRefreshState);

    return next(addToken(req, authStore.accessToken())).pipe(
        catchError(error => {
            // doar un token expirat/invalid duce la refresh (si eventual logout);
            // un 403 de drepturi se propaga catre componenta, sesiunea ramane activa
            if (error instanceof HttpErrorResponse && error.status === 403 && isTokenError(error)) {
                return handle403Error(req, next, authStore, authServ, refreshState);
            }
            return throwError(() => error);
        }),
    );
};

function handle403Error(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authStore: AuthStore,
    authServ: AuthService,
    refreshState: TokenRefreshState,
): Observable<HttpEvent<unknown>> {
    const logout = () => {
        // cererile care asteptau tokenul nou se incheie si ele cu eroare
        refreshState.refreshed.next(false);
        authServ.logout();
        return throwError(() => '');
    };

    if (refreshState.isRefreshing) {
        return refreshState.refreshed.pipe(
            filter(refreshed => refreshed !== null),
            take(1),
            switchMap(refreshed => refreshed
                ? next(addToken(req, authStore.accessToken()))
                : throwError(() => '')),
        );
    }

    refreshState.isRefreshing = true;
    refreshState.refreshed.next(null);
    return authServ.refreshToken(authStore.refreshToken()).pipe(
        catchError(() => logout()),
        finalize(() => {
            refreshState.isRefreshing = false;
        }),
        switchMap((response: ApiAuthResponse) => {
            if (response.token_type === 'bearer') {
                refreshState.refreshed.next(true);
                // erorile cererii reluate ajung la componenta, nu provoaca logout
                return next(addToken(req, authStore.accessToken()));
            }
            return logout();
        }),
    );
}
